import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { X, CheckCircle2, Mail, Phone, Home } from 'lucide-react';
import { Button } from './ui/button';

const PaymentSuccessModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-6 h-6 text-gray-500" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-12 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center animate-bounce">
              <CheckCircle2 className="w-16 h-16 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Ödemeniz Başarılı! 🎉
          </h1>
          <p className="text-xl text-green-50">
            Ödemeniz başarıyla alınmıştır
          </p>
        </div>

        {/* Content */}
        <div className="px-8 py-12">
          {/* Success Message */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Ödemeniz Alınmıştır
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-2">
              En kısa sürede iletişime geçeceğiz.
            </p>
            <p className="text-gray-500">
              Ödeme detayları ve randevu bilgileriniz e-posta adresinize gönderilecektir.
            </p>
          </div>

          {/* Info Cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">E-posta Bildirimi</h3>
                  <p className="text-sm text-gray-600">
                    Ödeme onayı ve detaylar e-posta adresinize gönderilecek
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Hızlı İletişim</h3>
                  <p className="text-sm text-gray-600">
                    Uzmanlarımız en kısa sürede sizinle iletişime geçecek
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 mb-8 border border-purple-100">
            <h3 className="font-semibold text-gray-900 mb-4 text-center">Öncelikli Hizmetleriniz</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl mb-2">⭐</div>
                <p className="text-sm font-medium text-gray-700">Öncelikli Hizmet</p>
              </div>
              <div>
                <div className="text-2xl mb-2">🎁</div>
                <p className="text-sm font-medium text-gray-700">Uzman Danışmanlık</p>
              </div>
              <div>
                <div className="text-2xl mb-2">⚡</div>
                <p className="text-sm font-medium text-gray-700">Hızlı Başlangıç</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => {
                onClose();
                navigate('/');
              }}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-6 text-lg"
            >
              <Home className="w-5 h-5 mr-2" />
              Ana Sayfaya Dön
            </Button>
            <Button
              onClick={() => {
                onClose();
                navigate('/iletisim');
              }}
              variant="outline"
              className="flex-1 border-2 border-purple-600 text-purple-600 hover:bg-purple-50 font-semibold py-6 text-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              İletişime Geç
            </Button>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Sorularınız için{' '}
              <Link to="/iletisim" className="text-purple-600 hover:text-purple-700 font-medium underline">
                iletişim sayfamızı
              </Link>
              {' '}ziyaret edebilirsiniz.
            </p>
          </div>
        </div>

        {/* Footer Note */}
        <div className="px-8 pb-8 text-center">
          <p className="text-sm text-gray-500">
            Ödeme işleminiz güvenli bir şekilde tamamlanmıştır. Teşekkür ederiz!
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;


